import { db } from "../db/indexeddb";
import {
  getAllVendorInvoices,
  getVendorInvoiceDetails,
} from "../../services/api/vendorInvoice.service";

const pickPositiveNumber = (...values) => {
  for (const value of values) {
    const numberValue = Number(value);
    if (Number.isFinite(numberValue) && numberValue >= 0) {
      return numberValue;
    }
  }
  return 0;
};

const normalizeVendorInvoiceList = (response = {}) => {
  const pagination = response?.pagination || {};
  const results = response?.results;
  let invoices = [];

  if (Array.isArray(results)) {
    invoices = results.flatMap((row) => row?.invoices || []);
  } else if (results && typeof results === "object") {
    invoices = results?.invoices || [];
  } else if (Array.isArray(response?.invoices)) {
    invoices = response.invoices;
  }

  return {
    count: pickPositiveNumber(
      response?.count,
      response?.total_count,
      response?.totalCount,
      response?.total_records,
      response?.totalRecords,
      pagination?.total_count,
      pagination?.totalCount,
      pagination?.total_records,
      pagination?.totalRecords,
      response?.page_count && response?.current_page_count
        ? Number(response.page_count) * Number(response.current_page_count)
        : 0,
      invoices.length
    ),
    next: response?.next || null,
    previous: response?.previous || null,
    results: results || [],
    invoices,
  };
};

export class VendorInvoiceRepository {
  constructor() {
    this.inflightListRequests = new Map();
    this.inflightDetailRequests = new Map();
  }

  async saveVendorInvoicesPage(
    org_token,
    page,
    pageSize,
    sortKey,
    sortOrder,
    normalizedResponse = {}
  ) {
    if (!org_token) return null;

    const normalizedPage = Number(page || 1);
    const key = `${org_token}_${normalizedPage}_${Number(pageSize || 10)}_${String(
      sortKey || ""
    )}_${String(sortOrder || "")}`;

    const record = {
      key,
      org_token: String(org_token),
      page: normalizedPage,
      page_size: Number(pageSize || 10),
      sort_key: String(sortKey || ""),
      sort_order: String(sortOrder || ""),
      count: Number(normalizedResponse?.count || 0),
      next: normalizedResponse?.next || null,
      previous: normalizedResponse?.previous || null,
      results: normalizedResponse?.results || [],
      invoices: normalizedResponse?.invoices || [],
      synced_at: Date.now(),
    };

    await db.vendor_invoice_list.put(record);
    return record;
  }

  async getVendorInvoicesPage(
    org_token,
    page = 1,
    pageSize = 10,
    sortKey = "",
    sortOrder = ""
  ) {
    if (!org_token) return null;
    const key = `${org_token}_${Number(page || 1)}_${Number(
      pageSize || 10
    )}_${String(sortKey || "")}_${String(sortOrder || "")}`;
    return db.vendor_invoice_list.get(key);
  }

  async fetchVendorInvoicesFromAPI(
    authToken,
    org_token,
    options = { page: 1, pageSize: 10 }
  ) {
    const page = Number(options?.page || 1);
    const pageSize = Number(options?.pageSize || 10);
    const sortKey = String(options?.sortKey || "");
    const sortOrder = String(options?.sortOrder || "");
    const requestKey = `${authToken || ""}|${org_token || ""}|${page}|${pageSize}|${sortKey}|${sortOrder}`;

    if (this.inflightListRequests.has(requestKey)) {
      return this.inflightListRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      const response = await getAllVendorInvoices(org_token, authToken, {
        page,
        pageSize,
        sortKey,
        sortOrder,
      });
      const normalized = normalizeVendorInvoiceList(response);
      // Don't block UI on IndexedDB write; persist in background.
      void this.saveVendorInvoicesPage(org_token, page, pageSize, sortKey, sortOrder, normalized).catch(
        () => {}
      );
      return normalized;
    })();

    this.inflightListRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.inflightListRequests.delete(requestKey);
    }
  }

  async saveVendorInvoiceDetails(token, page, response = {}) {
    if (!token) return null;

    const normalizedPage = Number(page || 1);
    const recordKey = `${String(token)}_${normalizedPage}`;

    const record = {
      token: recordKey,
      base_token: String(token),
      page: normalizedPage,
      organization: response?.organization || "",
      org_id: response?.org_id || "",
      invoices: response?.invoices || [],
      data: response || {},
      synced_at: Date.now(),
    };

    await db.vendor_invoice_details.put(record);
    return record;
  }

  async getVendorInvoiceDetails(token, page = 1) {
    if (!token) return null;
    const key = `${String(token)}_${Number(page || 1)}`;
    return db.vendor_invoice_details.get(key);
  }

  async fetchVendorInvoiceDetailsFromAPI(
    authToken,
    token,
    options = { page: 1 }
  ) {
    const page = Number(options?.page || 1);
    const requestKey = `${authToken || ""}|${token || ""}|${page}`;

    if (this.inflightDetailRequests.has(requestKey)) {
      return this.inflightDetailRequests.get(requestKey);
    }

    const requestPromise = (async () => {
      const response = await getVendorInvoiceDetails(token, authToken, {
        page,
      });
      // Don't block UI on IndexedDB write; persist in background.
      void this.saveVendorInvoiceDetails(token, page, response).catch(
        () => {}
      );
      return response;
    })();

    this.inflightDetailRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.inflightDetailRequests.delete(requestKey);
    }
  }
}

export const vendorInvoiceRepository = new VendorInvoiceRepository();

